import React, { useState, useCallback, useEffect, useRef } from 'react';
import { FileText, Swords, CheckSquare, MessageSquare, TrendingUp, GitBranch, ShieldCheck, Network, Sliders } from 'lucide-react';

const WAFFLE_TILES = [
  { Icon: FileText, title: 'IC Memo', sub: 'Synthesis' },
  { Icon: Swords, title: 'Bull vs Bear', sub: 'Deliberation' },
  { Icon: CheckSquare, title: '18-Factor', sub: 'IC Rubric' },
  { Icon: MessageSquare, title: 'Dally Chat', sub: 'Interrogate' },
  { Icon: TrendingUp, title: 'Growth Thesis', sub: 'Value Creation', highlight: true },
  { Icon: GitBranch, title: 'Provenance', sub: 'Cell-Level' },
  { Icon: ShieldCheck, title: 'Moat Audit', sub: 'Defensibility' },
  { Icon: Network, title: 'Strategic JVs', sub: '& Alliances' },
  { Icon: Sliders, title: 'Scenario Trees', sub: 'Downside Floor' },
];

const SLAB_Z = { 1: 0, 2: 32, 3: 64, 4: 96, 5: 128 };

const STACK_CSS = `
@keyframes stackFloat { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-8px); } }
@keyframes stackFloatShadow { 0%, 100% { opacity: 0.14; transform: scale(1); } 50% { opacity: 0.08; transform: scale(1.06); } }
@keyframes glowPulse { 0%, 100% { opacity: 0.4; } 50% { opacity: 0.75; } }
@keyframes shineSwipe { 0% { transform: translateX(-120%) rotate(20deg); } 100% { transform: translateX(120%) rotate(20deg); } }
@keyframes aosShimmer { 0%, 100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
@keyframes aosGlow { 0%, 100% { opacity: 0.55; transform: scale(1); } 50% { opacity: 0.95; transform: scale(1.08); } }
.waffle-tile {
  background: linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(245,248,252,0.88) 100%);
  border: 1.5px solid rgba(255,255,255,1);
  box-shadow: 0 6px 14px -3px rgba(0,0,0,0.06), inset 0 1px 2px rgba(255,255,255,1), inset 0 -2px 4px rgba(0,0,0,0.03);
  border-radius: 16px; padding: 10px; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.waffle-tile:hover { transform: translateZ(8px) scale(1.03); border-color: rgba(245, 130, 32, 0.6); box-shadow: 0 12px 22px -5px rgba(245,130,32,0.18), inset 0 1px 2px rgba(255,255,255,1), inset 0 -2px 4px rgba(0,0,0,0.03); }
.waffle-tile.is-amber { background: rgba(255, 246, 238, 0.6); border-color: rgba(245, 130, 32, 0.3); }
.stack-tag {
  background: linear-gradient(135deg, rgba(255,255,255,0.97) 0%, rgba(245,248,252,0.9) 100%);
  border: 1.5px solid rgba(255,255,255,1);
  box-shadow: 0 4px 10px -3px rgba(0,0,0,0.08), inset 0 1px 2px rgba(255,255,255,1), inset 0 -2px 4px rgba(0,0,0,0.03);
  border-radius: 10px; padding: 5px 11px; white-space: nowrap; transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.stack-tag:hover { transform: translateZ(6px) scale(1.05); border-color: rgba(245, 130, 32, 0.55); box-shadow: 0 9px 18px -4px rgba(245,130,32,0.18), inset 0 1px 2px rgba(255,255,255,1); }
.slab-dim-content { filter: blur(2px); opacity: 0.7; transition: filter 0.45s ease, opacity 0.45s ease; }
`;

// Interactive CSS 3D isometric Deal OS stack (reused from the landing architecture section).
export const DealAosStack = () => {
  const [activeLayer, setActiveLayer] = useState(5);
  const [hoveredLayer, setHoveredLayer] = useState(null);
  const [mounted, setMounted] = useState(false);
  const stackRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const smoothMouse = useRef({ x: 0, y: 0 });
  const rafRef = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 150);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    const el = stackRef.current;
    if (!el) return;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      mouseRef.current.x = (e.clientX - cx) / (rect.width / 2);
      mouseRef.current.y = (e.clientY - cy) / (rect.height / 2);
    };
    const onLeave = () => { mouseRef.current.x = 0; mouseRef.current.y = 0; };
    const tick = () => {
      const lerpF = 0.06;
      smoothMouse.current.x += (mouseRef.current.x - smoothMouse.current.x) * lerpF;
      smoothMouse.current.y += (mouseRef.current.y - smoothMouse.current.y) * lerpF;
      if (el) {
        const rx = smoothMouse.current.y * -4;
        const ry = smoothMouse.current.x * 5;
        el.style.setProperty('--mx', `${ry}deg`);
        el.style.setProperty('--my', `${rx}deg`);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    el.addEventListener('mouseleave', onLeave);
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
      cancelAnimationFrame(rafRef.current);
    };
  }, [mounted]);

  const selectLayer = useCallback((n) => setActiveLayer(n), []);
  const vis = (n) => mounted && n <= activeLayer;
  const act = (n) => n === activeLayer;
  const hov = (n) => hoveredLayer === n && !act(n);

  const slabTransform = (n) => {
    const isVis = vis(n);
    const isAct = act(n);
    const isHov = hov(n);
    const z = SLAB_Z[n] + (isAct ? 32 : isHov ? 16 : 0);
    const s = isAct ? 1.04 : isHov ? 1.01 : 0.97;
    if (!isVis) return `translateY(-500px) rotateX(58deg) rotateZ(-45deg) translateZ(${SLAB_Z[n]}px) scale(0.85)`;
    return `translateY(0px) rotateX(58deg) rotateZ(-45deg) translateZ(${z}px) scale(${s})`;
  };

  const slabTransition = (n) => {
    const isVis = vis(n);
    const showDelay = (n - 1) * 140;
    const hideDelay = (5 - n) * 100;
    const delay = isVis ? showDelay : hideDelay;
    return `transform 0.9s cubic-bezier(0.22, 1.0, 0.36, 1) ${delay}ms, opacity 0.6s ease ${delay}ms, box-shadow 0.5s cubic-bezier(0.22, 1, 0.36, 1), border-color 0.4s ease, filter 0.4s ease`;
  };

  const slabStyle = (n) => {
    const isVis = vis(n);
    const isAct = act(n);
    const isHov = hov(n);
    const base = {
      position: 'absolute', inset: 0, borderRadius: '36px', padding: '24px',
      display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
      transformStyle: 'preserve-3d', transform: slabTransform(n), transition: slabTransition(n),
      opacity: isVis ? (isAct ? 1 : isHov ? 0.92 : 0.78) : 0,
      zIndex: n * 10, cursor: 'pointer', pointerEvents: isVis ? 'auto' : 'none',
      willChange: 'transform, opacity', overflow: 'hidden',
      filter: isAct ? 'brightness(1)' : isHov ? 'brightness(1.02)' : 'brightness(0.97)',
    };
    if (n === 5) {
      return {
        ...base,
        background: isAct ? 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,255,0.95) 100%)' : 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(245,248,252,0.88) 100%)',
        border: isAct ? '2px solid rgba(15,23,42,0.55)' : isHov ? '2px solid rgba(200,210,230,0.8)' : '2px solid rgba(255,255,255,0.85)',
        boxShadow: isAct ? '0 0 50px -5px rgba(15,23,42,0.3), 0 40px 70px -15px rgba(15,23,42,0.2), inset 0 2px 4px rgba(255,255,255,0.95), inset 0 -3px 8px rgba(0,0,0,0.04)' : isHov ? '0 0 25px -5px rgba(150,170,210,0.15), 0 35px 60px -12px rgba(15,23,42,0.15), inset 0 2px 4px rgba(255,255,255,0.9), inset 0 -3px 8px rgba(0,0,0,0.03)' : '0 30px 55px -12px rgba(15,23,42,0.12), inset 0 2px 4px rgba(255,255,255,0.85), inset 0 -3px 8px rgba(0,0,0,0.02)',
      };
    }
    if (n === 1) {
      return {
        ...base,
        background: 'linear-gradient(135deg, rgba(148,163,184,0.95) 0%, rgba(168,182,200,0.92) 100%)',
        border: isAct ? '2px solid rgba(15,23,42,0.55)' : isHov ? '2px solid rgba(200,210,230,0.8)' : '2px solid rgba(255,255,255,0.6)',
        boxShadow: isAct ? '0 0 50px -5px rgba(15,23,42,0.3), 0 35px 60px -12px rgba(15,23,42,0.18), inset 0 2px 4px rgba(255,255,255,0.5), inset 0 -3px 8px rgba(0,0,0,0.06)' : isHov ? '0 0 25px -5px rgba(150,170,210,0.15), 0 30px 55px -12px rgba(15,23,42,0.14), inset 0 2px 4px rgba(255,255,255,0.4), inset 0 -3px 8px rgba(0,0,0,0.04)' : '0 25px 50px -12px rgba(15,23,42,0.1), inset 0 2px 4px rgba(255,255,255,0.35), inset 0 -3px 8px rgba(0,0,0,0.03)',
      };
    }
    return {
      ...base,
      background: isAct ? 'linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(245,248,255,0.82) 100%)' : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(238,242,250,0.7) 100%)',
      backdropFilter: 'blur(8px)',
      border: isAct ? '2px solid rgba(15,23,42,0.55)' : isHov ? '2px solid rgba(200,210,230,0.8)' : '2px solid rgba(255,255,255,0.85)',
      boxShadow: isAct ? '0 0 50px -5px rgba(15,23,42,0.3), 0 35px 60px -12px rgba(15,23,42,0.18), inset 0 2px 4px rgba(255,255,255,0.95), inset 0 -3px 8px rgba(0,0,0,0.04)' : isHov ? '0 0 25px -5px rgba(150,170,210,0.15), 0 30px 55px -12px rgba(15,23,42,0.14), inset 0 2px 4px rgba(255,255,255,0.9), inset 0 -3px 8px rgba(0,0,0,0.03)' : '0 25px 50px -12px rgba(15,23,42,0.1), inset 0 2px 4px rgba(255,255,255,0.85), inset 0 -3px 8px rgba(0,0,0,0.02)',
    };
  };

  const shineOverlay = (n) => ({
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: '36px',
    pointerEvents: 'none', overflow: 'hidden', opacity: act(n) ? 0.5 : 0, transition: 'opacity 0.6s ease',
  });

  const Shine = ({ n, strength = 0.08 }) => (
    <div style={shineOverlay(n)}>
      <div style={{ position: 'absolute', top: '-50%', left: '-50%', width: '200%', height: '200%', background: `linear-gradient(90deg, transparent 30%, rgba(255,255,255,${strength}) 50%, transparent 70%)`, animation: act(n) ? 'shineSwipe 3s ease-in-out infinite' : 'none' }} />
    </div>
  );

  return (
    <>
      <style>{STACK_CSS}</style>
      <div ref={stackRef} className="flex justify-center items-center pt-44 sm:pt-52 pb-8 lg:-translate-x-4 xl:-translate-x-8" style={{ perspective: '2200px', perspectiveOrigin: '50% 40%' }}>
        <div className="relative w-[300px] xl:w-[360px] h-[300px] xl:h-[360px]" style={{ transformStyle: 'preserve-3d', transform: 'rotateY(var(--mx, 0deg)) rotateX(var(--my, 0deg))', transition: 'transform 0.1s ease-out' }}>
          <div className="absolute inset-0" style={{ transformStyle: 'preserve-3d', animation: mounted ? 'stackFloat 6s cubic-bezier(0.45, 0, 0.55, 1) infinite' : 'none' }}>

            <div className="absolute inset-4 rounded-full pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, rgba(15,23,42,0.16) 0%, transparent 72%)', filter: 'blur(35px)', animation: mounted ? 'stackFloatShadow 6s cubic-bezier(0.45, 0, 0.55, 1) infinite' : 'none' }} />
            <div className="absolute pointer-events-none" style={{ left: '10%', right: '10%', bottom: '-5%', height: '50%', transform: 'rotateX(58deg) rotateZ(-45deg) translateZ(-10px) scaleY(-0.3)', background: 'linear-gradient(to bottom, rgba(200,210,230,0.08), transparent)', filter: 'blur(12px)', opacity: 0.5, borderRadius: '36px' }} />
            <div className="absolute inset-0 rounded-[36px] pointer-events-none" style={{ transform: `rotateX(58deg) rotateZ(-45deg) translateZ(${SLAB_Z[activeLayer] + 31}px)`, boxShadow: '0 0 70px 10px rgba(15,23,42,0.12)', transition: 'transform 0.9s cubic-bezier(0.22, 1.0, 0.36, 1)', animation: 'glowPulse 3s ease-in-out infinite' }} />

            {/* L1 */}
            <div onClick={() => selectLayer(1)} onMouseEnter={() => setHoveredLayer(1)} onMouseLeave={() => setHoveredLayer(null)} style={slabStyle(1)}>
              <Shine n={1} strength={0.06} />
              <div className="absolute inset-0 pointer-events-none rounded-[36px]" style={{ background: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)', backgroundSize: '16px 16px' }} />
              <div className="flex items-center justify-between relative z-10">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded-lg bg-[#F58220]/20 border border-[#F58220]/40 text-[10px] font-mono font-bold text-[#F58220]">L1</span>
                  <span className="font-bold text-xs tracking-tight text-slate-900 font-mono">StrategyCore&#8482;</span>
                </div>
                <span className="text-[9px] font-mono bg-slate-700/15 text-slate-700 px-2 py-0.5 rounded-full hidden sm:inline">L1 // STRATEGIC REASONING SLMS</span>
              </div>
              <div className={`flex-1 flex flex-col items-center justify-center relative z-10 ${act(1) ? '' : 'slab-dim-content'}`}>
                <span className="text-base sm:text-lg font-extrabold text-slate-800 tracking-tight font-mono">Off-LLM Deterministic Math</span>
              </div>
              <div className={`flex items-center justify-center flex-wrap gap-1.5 border-t border-slate-400/30 pt-3 relative z-10 ${act(1) ? '' : 'slab-dim-content'}`}>
                <span className="stack-tag text-[9px] font-mono font-semibold text-slate-600">20Y M&amp;A Heuristics</span>
                <span className="stack-tag text-[9px] font-mono font-semibold text-slate-600">CDD Hypothesis Logic</span>
                <span className="stack-tag text-[9px] font-mono font-semibold text-[#F58220]">100% Deterministic</span>
              </div>
            </div>

            {/* L2 */}
            <div onClick={() => selectLayer(2)} onMouseEnter={() => setHoveredLayer(2)} onMouseLeave={() => setHoveredLayer(null)} style={slabStyle(2)}>
              <Shine n={2} />
              <div className="flex items-center justify-between relative z-10">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded-lg bg-slate-900/10 border border-slate-300 text-[10px] font-mono font-bold text-slate-800">L2</span>
                  <span className="font-bold text-xs tracking-tight text-slate-900 font-mono">OmniContext&#8482;</span>
                </div>
                <span className="text-[9px] font-mono bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full font-semibold hidden sm:inline">L2 // SOVEREIGN DEAL GRAPH</span>
              </div>
              <div className={`flex-1 flex flex-col items-center justify-center relative z-10 ${act(2) ? '' : 'slab-dim-content'}`}>
                <span className="text-base sm:text-lg font-extrabold text-slate-800 tracking-tight font-mono">Multi-Format VDR Ingest</span>
              </div>
              <div className={`flex items-center justify-center flex-wrap gap-1.5 border-t border-slate-200/80 pt-3 relative z-10 ${act(2) ? '' : 'slab-dim-content'}`}>
                <span className="stack-tag text-[9px] font-mono font-semibold text-slate-600">Cell-to-Source Lineage</span>
                <span className="stack-tag text-[9px] font-mono font-semibold text-slate-600">Zero Context Drift</span>
                <span className="stack-tag text-[9px] font-mono font-semibold text-[#F58220]">Air-Gapped Enclave</span>
              </div>
            </div>

            {/* L3 */}
            <div onClick={() => selectLayer(3)} onMouseEnter={() => setHoveredLayer(3)} onMouseLeave={() => setHoveredLayer(null)} style={slabStyle(3)}>
              <Shine n={3} />
              <div className="flex items-center justify-between relative z-10">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded-lg bg-slate-900/10 border border-slate-300 text-[10px] font-mono font-bold text-slate-800">L3</span>
                  <span className="font-bold text-xs tracking-tight text-slate-900 font-mono">SmartOrch&#8482;</span>
                </div>
                <span className="text-[9px] font-mono bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full font-semibold hidden sm:inline">MULTI-AGENT DAG CHOREOGRAPHY</span>
              </div>
              <div className={`flex-1 flex flex-col items-center justify-center relative z-10 ${act(3) ? '' : 'slab-dim-content'}`}>
                <span className="text-base sm:text-lg font-extrabold text-slate-800 tracking-tight font-mono">Context-Locked DAGs</span>
              </div>
              <div className={`flex items-center justify-center flex-wrap gap-1.5 border-t border-slate-200/80 pt-3 relative z-10 ${act(3) ? '' : 'slab-dim-content'}`}>
                <span className="stack-tag text-[9px] font-mono font-semibold text-slate-600">Bull vs. Bear Protocols</span>
                <span className="stack-tag text-[9px] font-mono font-semibold text-slate-600">48h CDD Verdicts</span>
                <span className="stack-tag text-[9px] font-mono font-semibold text-[#F58220]">Consensus Protocol Active</span>
              </div>
            </div>

            {/* L4 */}
            <div onClick={() => selectLayer(4)} onMouseEnter={() => setHoveredLayer(4)} onMouseLeave={() => setHoveredLayer(null)} style={slabStyle(4)}>
              <Shine n={4} />
              <div className="flex items-center justify-between relative z-10 pb-3 border-b border-slate-200/80">
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-0.5 rounded-lg bg-[#F58220] text-[10px] font-mono font-bold text-white shadow-sm">L4</span>
                  <span className="font-extrabold text-xs tracking-tight text-slate-900 font-mono">DealSpace&#8482;</span>
                </div>
                <div className="flex items-center gap-1.5 text-[9px] font-mono font-bold text-[#F58220] bg-[#F58220]/15 px-2 py-0.5 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#F58220] animate-ping" />
                  <span className="hidden sm:inline">COCKPIT INTERFACES</span>
                </div>
              </div>
              <div className={`grid grid-cols-3 gap-2.5 my-auto relative z-10 ${act(4) ? '' : 'slab-dim-content'}`}>
                {WAFFLE_TILES.map((tile) => (
                  <div key={tile.title} className={`waffle-tile group/tile ${tile.highlight ? 'is-amber' : ''}`}>
                    <tile.Icon size={16} className={`mb-1 ${tile.highlight ? 'text-[#F58220]' : 'text-slate-700 group-hover/tile:text-[#F58220] transition-colors'}`} />
                    <span className={`text-[10px] font-bold leading-tight ${tile.highlight ? 'text-slate-900' : 'text-slate-800'}`}>{tile.title}</span>
                    <span className={`text-[8px] font-mono mt-0.5 ${tile.highlight ? 'text-[#F58220] font-semibold' : 'text-slate-500'}`}>{tile.sub}</span>
                  </div>
                ))}
              </div>
              <div className={`flex items-center justify-between text-[10px] font-mono text-slate-700 border-t border-slate-200/80 pt-2.5 relative z-10 ${act(4) ? '' : 'slab-dim-content'}`}>
                <span className="font-semibold text-slate-900">48-Hour IC Turnaround</span>
                <span className="text-[#F58220] font-bold">Audit-Grade Lineage</span>
              </div>
            </div>

            {/* L5 */}
            <div onClick={() => selectLayer(5)} onMouseEnter={() => setHoveredLayer(5)} onMouseLeave={() => setHoveredLayer(null)} style={slabStyle(5)}>
              <Shine n={5} />
              <div className="flex-1 flex items-center justify-center relative z-10">
                <div className="relative flex items-center justify-center group/aos cursor-pointer">
                  <div className="absolute -inset-x-8 -inset-y-5 rounded-full bg-[#F58220]/15 blur-2xl pointer-events-none" style={{ animation: 'aosGlow 3.5s ease-in-out infinite' }} />
                  <span className="relative text-xl sm:text-2xl lg:text-[26px] font-black tracking-tight font-mono bg-clip-text text-transparent transition-transform duration-300 group-hover/aos:scale-110" style={{ backgroundImage: 'linear-gradient(110deg, #0F172A 0%, #334155 26%, #F58220 50%, #334155 74%, #0F172A 100%)', backgroundSize: '220% 100%', animation: 'aosShimmer 4.5s ease-in-out infinite', filter: 'drop-shadow(0 1px 1px rgba(255,255,255,0.7))' }}>
                    Frontier Deal OS<span style={{ WebkitTextFillColor: '#F58220', color: '#F58220' }}>&#8482;</span>
                  </span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};
